import styles from "./Store.module.scss";
import { Title2, SubTitle3 } from "@components/new/Typo";
import { MarketplaceStoreGroup } from "@components/new/MarketplaceStoreGroup";
import { Marketplace } from "@components/new/Marketplace";
import { Store } from "@components/new/Store";
import {
  IconButton,
  IconButtonSize,
  IconButtonType,
} from "@components/new/IconButton";
import EditIcon from "@assets/icons/outline/square-pen.svg?react";
import DelIcon from "@assets/icons/outline/trash-2.svg?react";
import { Tooltip } from "@components/new/Tooltip";
import EditShopModal from "../EditShopModal";
import { deleteSeller, getMarketplaceId, Seller } from "@api/apiClient/sellers";
import ConfirmModal from "@components/modals/ConfirmModal";
import { useState, useEffect } from "react";
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import WarningIcon from "@assets/icons/solid/circle-alert.svg?react";
import cn from "classnames";

type TStoreProps = {
  seller: Seller;
};

const trimApiKey = (value?: string) =>
  value?.length! > 9 ? `${String(value).slice(0, 9)}...` : value;

const getSellerInfo = (seller: Seller) => {
  if (seller.apiKey || seller.apiClientId)
    return {
      apiKey: trimApiKey(seller.apiKey),
      apiClientId: seller.apiClientId,
    };
  if (seller.ymSeller?.apiKey)
    return {
      apiKey: trimApiKey(seller.ymSeller.apiKey),
    };
  if (seller.wbSeller?.token)
    return {
      apiKey: trimApiKey(seller.wbSeller.token),
    };
  if (seller.aliSeller?.token)
    return {
      apiKey: trimApiKey(seller.aliSeller.token),
    };
  return {};
};

const StoreItem: React.FC<TStoreProps> = (
  props: TStoreProps,
): React.ReactElement => {
  const { seller } = props;
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const storeId = searchParams.get("storeId");

  const [isOpenEditModal, setIsOpenEditModal] = useState<boolean>(false);
  const [isOpenDeleteWindow, setIsOpenDeleteWindow] = useState<boolean>(false);

  const { t } = useTranslation();

  useEffect(() => {
    if (storeId === seller.id) {
      setIsOpenEditModal(true);
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.delete("storeId");
        return newParams;
      });
    }
  }, []);

  const isOzon = !seller.wbSeller && !seller.ymSeller && !seller.aliSeller;
  // Удалять можно Ozon, WB и YM (Ali — нет). Для WB/YM бэк снимает allowedToDelete,
  // пока у магазина есть активные FBP-связки.
  const canShowDelete = isOzon || Boolean(seller.wbSeller || seller.ymSeller);
  const storesLinked = isOzon
    ? !seller.allowedToDeleteOzon
    : !seller.allowedToDelete;

  const sellerInfo = getSellerInfo(seller);

  const tokenStatus = seller.wbSeller?.tokenStatus ?? seller.aliSeller?.tokenStatus;

  const ExpiredOrInvalidKey = ["Expired", "Invalid"].find(
    (v) => v === tokenStatus,
  )

  return (
    <>
      <div key={seller.id} className={cn(styles.store, ExpiredOrInvalidKey && styles.storeInvalid)}>
        <MarketplaceStoreGroup>
          <Store id={seller.id} name={seller.name} />
          <Marketplace name={getMarketplaceId(seller)} />
        </MarketplaceStoreGroup>

        <div className={styles.infoBlock}>
          <Title2 ellipsis>{seller.name}</Title2>
          {seller.email && (
            <SubTitle3 ellipsis color="grey-800">
              {seller.email}
            </SubTitle3>
          )}
        </div>
        <div className={styles.separator} />
        <div className={styles.infoBlock}>
          {sellerInfo.apiKey && (
            <SubTitle3 color="grey-800">
              API-key: {sellerInfo.apiKey ?? ""}
            </SubTitle3>
          )}
          {sellerInfo.apiClientId && (
            <SubTitle3 color="grey-800">
              Client ID: {sellerInfo.apiClientId ?? ""}
            </SubTitle3>
          )}
        </div>
        <div className={styles.separator} />
        {["Expiring", "Expired", "Invalid"].find(
          (v) => v === tokenStatus,
        ) ? (
          <div className={styles.infoBlock}>
            <div className={styles.status}>
              <WarningIcon
                className={cn(
                  styles.icon,
                  styles[`icon-${tokenStatus}`],
                )}
              />
              <SubTitle3>
                {t(`storesPage.tokenStatus.${tokenStatus}`)}
              </SubTitle3>
            </div>
          </div>
        ) : null}

        <div className={styles.actions}>
          {seller.wbSeller || seller.aliSeller || isOzon ? (
            <IconButton
              icon={EditIcon}
              onClick={() => setIsOpenEditModal(true)}
              size={IconButtonSize.L}
              type={IconButtonType.ICON}
            />
          ) : null}
          {canShowDelete ? (
            <Tooltip
              action={
                <IconButton
                  className={styles.action}
                  icon={DelIcon}
                  onClick={() => setIsOpenDeleteWindow(true)}
                  disabled={Boolean(storesLinked)}
                  size={IconButtonSize.L}
                  type={IconButtonType.ICON}
                />
              }
              position="top"
              align="start"
              text={storesLinked ? t("storesPage.storesAlreadyLinked") : ""}
            />
          ) : null}
        </div>
      </div>

      {isOpenEditModal ? (
        <EditShopModal
          onClose={() => {
            setIsOpenEditModal(false);
          }}
          seller={seller}
        />
      ) : null}

      {isOpenDeleteWindow && (
        <ConfirmModal
          key={seller.id}
          title={t("storesPage.deleteSellerModal.title")}
          description={t("storesPage.deleteSellerModal.description", {
            name: seller.name,
          })}
          noBtnTitle={t("storesPage.deleteSellerModal.cancelBtn")}
          yesBtnTitle={t("storesPage.deleteSellerModal.okBtn")}
          onClose={() => setIsOpenDeleteWindow(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: [] });
          }}
          requestFn={() => deleteSeller(seller.id)}
        />
      )}
    </>
  );
};

export default StoreItem;
